// @ts-nocheck — pre-existing file, React Flow types need alignment
import React, { useState, useEffect, useCallback } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SchemaNodeComponent from '../../components/data-integration/SchemaNodeComponent';
import { getErDiagramData } from '../../services/data-integration-api';

const nodeTypes = { schemaNode: SchemaNodeComponent };

interface ErDiagramProps {
  connectionId: string;
  onBack?: () => void;
}

/**
 * ER 关系图页面 — React Flow + ELK.js 自动布局
 */
export default function ErDiagram({ connectionId, onBack }: ErDiagramProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total_nodes: 0, total_edges: 0 });

  const loadDiagram = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getErDiagramData(connectionId);
      setStats({ total_nodes: data.total_nodes, total_edges: data.total_edges });

      // 使用 ELK.js 布局（如果可用），否则使用简单网格布局
      let layoutNodes: Node[];
      try {
        const ELK = (await import('elkjs/lib/elk.bundled.js')).default;
        const elk = new ELK();

        const graph = {
          id: 'root',
          layoutOptions: {
            'elk.algorithm': 'layered',
            'elk.direction': 'RIGHT',
            'elk.spacing.nodeNode': '60',
            'elk.layered.spacing.nodeNodeBetweenLayers': '80',
          },
          children: data.nodes.map(n => ({
            id: n.id,
            width: 200,
            height: 40 + Math.max(n.key_columns.length, 1) * 24,
          })),
          edges: data.edges.map(e => ({
            id: e.id,
            sources: [e.source_node_id],
            targets: [e.target_node_id],
          })),
        };

        const layout = await elk.layout(graph);

        layoutNodes = data.nodes.map(n => {
          const elkNode = layout.children?.find(c => c.id === n.id);
          return {
            id: n.id,
            type: 'schemaNode',
            position: { x: elkNode?.x || 0, y: elkNode?.y || 0 },
            data: n,
          };
        });
      } catch {
        // Fallback: 简单网格布局
        const cols = Math.ceil(Math.sqrt(data.nodes.length));
        layoutNodes = data.nodes.map((n, i) => ({
          id: n.id,
          type: 'schemaNode',
          position: { x: (i % cols) * 280, y: Math.floor(i / cols) * 200 },
          data: n,
        }));
      }

      const flowEdges: Edge[] = data.edges.map(e => ({
        id: e.id,
        source: e.source_node_id,
        target: e.target_node_id,
        label: e.constraint_name,
        type: 'smoothstep',
        animated: false,
        style: { stroke: 'var(--color-muted-foreground)' },
      }));

      setNodes(layoutNodes);
      setEdges(flowEdges);
    } catch (e: any) {
      setError(e.message || '加载 ER 图失败');
    } finally {
      setLoading(false);
    }
  }, [connectionId, setNodes, setEdges]);

  useEffect(() => {
    loadDiagram();
  }, [loadDiagram]);

  // 搜索高亮并定位
  const handleSearch = useCallback(() => {
    if (!searchTerm) {
      setNodes(nds => nds.map(n => ({ ...n, style: undefined })));
      return;
    }
    const term = searchTerm.toLowerCase();
    setNodes(nds => nds.map(n => {
      const match = n.data.table_name.toLowerCase().includes(term);
      return {
        ...n,
        style: match ? { border: '3px solid #ff6b00', borderRadius: 8 } : { opacity: 0.4 },
      };
    }));
  }, [searchTerm, setNodes]);

  if (loading) return <div className="p-6 text-muted-foreground">加载 ER 关系图...</div>;
  if (error) return <div className="p-6 text-destructive">错误: {error}</div>;

  return (
    <div className="h-screen flex flex-col">
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <h3 className="font-semibold">ER 关系图</h3>
          <span className="text-sm text-muted-foreground">
            {stats.total_nodes} 张表 | {stats.total_edges} 个关系
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="搜索表名..."
            className="w-52"
          />
          <Button variant="outline" size="sm" onClick={handleSearch}>搜索</Button>
          <Button variant="outline" size="sm" onClick={() => { setSearchTerm(''); setNodes(nds => nds.map(n => ({ ...n, style: undefined }))); }}>
            重置
          </Button>
        </div>
      </div>

      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.1}
          maxZoom={2}
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
}
